import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Search, Crown, User, Trash2, Edit, Shield, UserX, UserCheck, Ban, Star } from 'lucide-react';
import api from '../lib/api';

export default function AdminUsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteWarning, setDeleteWarning] = useState(null);

  useEffect(() => {
    if (!user?.admin_role) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate, search, filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('subscription', filter);
      
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (u) => {
    setSelectedUser(u);
    setEditData({
      subscription: u.subscription || 'free',
      months: 1
    });
    setShowEditModal(true);
  };

  const handleSaveSubscription = async () => {
    try {
      // Use the dedicated subscription endpoint
      await api.post(`/admin/users/${selectedUser.user_id}/subscription`, {
        subscription: editData.subscription,
        months: parseInt(editData.months) || 1
      });
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleToggleStatus = async () => {
    try {
      await api.post(`/admin/users/${selectedUser.user_id}/toggle-status`);
      setShowDeactivateModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const handleDeleteUser = async (force = false) => {
    try {
      const response = await api.delete(`/admin/users/${selectedUser.user_id}${force ? '?force=true' : ''}`);
      
      // Check if backend returned a warning (user is family owner)
      if (response.data.warning && response.data.requires_force) {
        setDeleteWarning(response.data);
        return;
      }
      
      // Success - close modal and refresh
      setShowDeleteModal(false);
      setDeleteWarning(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteWarning(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Gestion Utilisateurs</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Search & Filter */}
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par email ou nom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'Tous' },
              { value: 'free', label: 'Gratuit' },
              { value: 'standard', label: 'Standard' },
              { value: 'premium', label: 'Premium' }
            ].map((f) => (
              <Button
                key={f.value}
                variant={filter === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Users List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            ) : (
              <div className="divide-y divide-border">
                {users.map((u) => (
                  <div key={u.user_id} className={`p-4 flex items-center justify-between hover:bg-muted/50 ${u.is_active === false ? 'opacity-50 bg-red-50' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center relative">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" />
                        )}
                        {u.is_active === false && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                            <Ban className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.name}</span>
                          {u.subscription === 'premium' && (
                            <Crown className="w-4 h-4 text-yellow-500" />
                          )}
                          {u.subscription === 'standard' && (
                            <Star className="w-4 h-4 text-blue-500" />
                          )}
                          {u.admin_role && (
                            <Shield className="w-4 h-4 text-primary" />
                          )}
                          {u.is_active === false && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">Désactivé</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Edit Subscription */}
                      <Button variant="ghost" size="icon" onClick={() => handleEditUser(u)} title="Modifier abonnement">
                        <Edit className="w-4 h-4" />
                      </Button>
                      
                      {/* Activate/Deactivate */}
                      {u.user_id !== user.user_id && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className={u.is_active === false ? "text-green-500 hover:text-green-600" : "text-orange-500 hover:text-orange-600"}
                          onClick={() => { setSelectedUser(u); setShowDeactivateModal(true); }}
                          title={u.is_active === false ? "Réactiver le compte" : "Désactiver le compte"}
                        >
                          {u.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </Button>
                      )}
                      
                      {/* Delete */}
                      {user?.admin_role === 'super_admin' && u.user_id !== user.user_id && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-500 hover:text-red-600"
                          onClick={() => { setSelectedUser(u); setShowDeleteModal(true); }}
                          title="Supprimer définitivement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Subscription Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier Abonnement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Utilisateur: {selectedUser?.email}</p>
            <div>
              <label className="text-sm font-medium">Type d'abonnement</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[
                  { value: 'free', label: 'Gratuit', color: 'text-gray-500' },
                  { value: 'standard', label: 'Standard', color: 'text-blue-500' },
                  { value: 'premium', label: 'Premium', color: 'text-yellow-500' }
                ].map((sub) => (
                  <Button
                    key={sub.value}
                    variant={editData.subscription === sub.value ? 'default' : 'outline'}
                    onClick={() => setEditData({ ...editData, subscription: sub.value })}
                    className={editData.subscription === sub.value ? '' : sub.color}
                  >
                    {sub.label}
                  </Button>
                ))}
              </div>
            </div>
            {(editData.subscription === 'premium' || editData.subscription === 'standard') && (
              <div>
                <label className="text-sm font-medium">Durée (mois)</label>
                <Input
                  type="number"
                  min="1"
                  value={editData.months}
                  onChange={(e) => setEditData({ ...editData, months: parseInt(e.target.value) || 1 })}
                  className="mt-2"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button onClick={handleSaveSubscription}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Modal */}
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.is_active === false ? 'Réactiver le compte' : 'Désactiver le compte'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedUser?.is_active === false ? (
              <p>Voulez-vous réactiver le compte de <strong>{selectedUser?.email}</strong> ?</p>
            ) : (
              <>
                <p>Voulez-vous désactiver le compte de <strong>{selectedUser?.email}</strong> ?</p>
                <p className="text-sm text-muted-foreground mt-2">
                  L'utilisateur ne pourra plus se connecter mais ses données seront conservées.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateModal(false)}>Annuler</Button>
            <Button 
              variant={selectedUser?.is_active === false ? "default" : "destructive"} 
              onClick={handleToggleStatus}
            >
              {selectedUser?.is_active === false ? 'Réactiver' : 'Désactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={handleCancelDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer l'utilisateur</DialogTitle>
          </DialogHeader>
          
          {deleteWarning ? (
            // Show warning for family owner
            <div className="py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium mb-2">⚠️ Attention : Propriétaire de foyer</p>
                <p className="text-amber-700 text-sm">{deleteWarning.message}</p>
                {deleteWarning.family_name && (
                  <p className="text-amber-600 text-sm mt-2">
                    Foyer : <strong>{deleteWarning.family_name}</strong> ({deleteWarning.members_count} membre(s))
                  </p>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                En confirmant, le foyer sera supprimé et tous les membres seront déconnectés du foyer.
              </p>
            </div>
          ) : (
            // Normal delete confirmation
            <p className="py-4">
              ⚠️ Êtes-vous sûr de vouloir supprimer <strong>{selectedUser?.email}</strong> et <strong>toutes ses données</strong> définitivement ?
              <br /><br />
              <span className="text-sm text-muted-foreground">Cette action est irréversible.</span>
            </p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>Annuler</Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteUser(deleteWarning ? true : false)}
            >
              {deleteWarning ? 'Confirmer la suppression' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
